<template>
  <div class="word-table-container">
    <!-- 顶部搜索栏 -->
    <div class="search-bar">
      <form class="search-form">
        <div class="form-item">
          <label>单词书:</label>
          <div class="custom-book-select">
            <div
              class="select-trigger"
              @click="toggleDropdown"
              :class="{ active: dropdownVisible }"
            >
              <span class="select-value">{{
                selectedBookName || "请选择单词书"
              }}</span>
              <i class="select-arrow" :class="{ 'arrow-up': dropdownVisible }"
                >▼</i
              >
            </div>

            <div v-if="dropdownVisible" class="select-dropdown">
              <div class="dropdown-container">
                <!-- 左侧分类 -->
                <div class="book-categories">
                  <div
                    v-for="group in bookList"
                    :key="group.bookType"
                    class="category-item"
                    :class="{ active: selectedCategory === group.bookType }"
                    @click="selectCategory(group.bookType)"
                  >
                    {{ group.bookTypeInfo }}
                  </div>
                </div>

                <!-- 右侧单词书选项 -->
                <div class="book-options">
                  <div
                    v-for="book in currentBooks"
                    :key="book.bookId"
                    class="book-option"
                    :class="{ selected: searchForm.bookId === book.bookId }"
                    @click="selectBook(book)"
                  >
                    {{ book.bookName }}
                  </div>

                  <div v-if="currentBooks.length === 0" class="no-books">
                    请先选择左侧分类
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="form-item">
          <label>Idol:</label>
          <div class="custom-idol-select">
            <div
              class="select-trigger"
              @click="toggleIdolDropdown"
              :class="{ active: idolDropdownVisible }"
            >
              <span class="select-value">{{
                selectedIdolName || "请选择Idol"
              }}</span>
              <i
                class="select-arrow"
                :class="{ 'arrow-up': idolDropdownVisible }"
                >▼</i
              >
            </div>

            <div v-if="idolDropdownVisible" class="select-dropdown">
              <div class="dropdown-container">
                <!-- 左侧团体名称 -->
                <div class="idol-groups">
                  <div
                    v-for="group in idolList"
                    :key="group.groupId"
                    class="group-item"
                    :class="{ active: selectedGroup === group.groupId }"
                    @click="selectGroup(group.groupId)"
                  >
                    {{ group.groupName }}
                  </div>
                </div>

                <!-- 右侧爱豆选项 -->
                <div class="idol-options">
                  <div
                    v-for="idol in currentIdols"
                    :key="idol.idolId"
                    class="idol-option"
                    :class="{ selected: searchForm.idolId === idol.idolId }"
                    @click="selectIdol(idol)"
                  >
                    {{ idol.idolName }}
                  </div>

                  <div v-if="currentIdols.length === 0" class="no-idols">
                    请先选择左侧团体
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="form-item">
          <label>单词搜索:</label>
          <div class="search-input-group">
            <input
              type="text"
              v-model="searchForm.content"
              class="form-input search-input"
              placeholder="请输入要搜索的单词"
              @keyup.enter="loadWords"
            />
            <button
              v-if="searchForm.content"
              type="button"
              class="btn btn-clear"
              @click="clearSearch"
              title="清空搜索"
            >
              ✕
            </button>
          </div>
        </div>

        <div class="form-item">
          <button
            type="button"
            class="btn btn-primary"
            @click="loadWords"
            :disabled="loading"
          >
            {{ loading ? "搜索中..." : "搜索" }}
          </button>
        </div>
      </form>

      <!-- 分页控制 -->
      <div class="pagination-controls">
        <button
          type="button"
          class="btn btn-pagination"
          :disabled="currentPage <= 1"
          @click="previousPage"
        >
          上一页
        </button>
        <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
        <button
          type="button"
          class="btn btn-pagination"
          :disabled="currentPage >= totalPages"
          @click="nextPage"
        >
          下一页
        </button>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="content-area">
      <!-- 左侧单词列表 -->
      <div class="word-list-container">
        <div class="word-list-card">
          <div class="card-header">
            <span>单词列表</span>
            <span class="word-count">共 {{ total }} 个单词</span>
          </div>

          <div class="card-body">
            <div class="word-list" :class="{ loading: loading }">
              <div
                v-for="word in wordList"
                :key="word.id"
                class="word-item"
                :class="{ active: selectedWord?.id === word.id }"
                @click="selectWord(word)"
              >
                <div class="word-content">{{ word.content }}</div>
                <div class="id">#{{ word.id }}</div>
                <div class="word-id">@{{ word.wordId }}</div>
              </div>

              <div v-if="wordList.length === 0 && !loading" class="empty-state">
                <div class="empty-content">
                  <div class="empty-icon">📝</div>
                  <div class="empty-text">暂无单词数据</div>
                </div>
              </div>

              <div v-if="loading" class="loading-state">
                <div class="loading-spinner"></div>
                <div class="loading-text">加载中...</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 右侧单词详情 -->
      <div class="word-detail-container">
        <div class="word-detail-card">
          <div class="card-header">
            <span>单词详情</span>
            <div v-if="selectedWord" class="word-actions">
              <button
                type="button"
                class="btn btn-primary btn-small"
                @click="openEditDialog"
              >
                ✏️ 编辑详情
              </button>
            </div>
          </div>

          <div class="card-body">
            <div class="word-detail" v-if="selectedWord">
              <!-- 单词和读音显示区域 -->
              <div class="word-pronunciation-section">
                <div class="word-main">
                  <span class="word-content">{{ selectedWord.content }}</span>

                  <!-- 音频播放器 -->
                  <div v-if="currentWordAudioUrl" class="word-audio-section">
                    <audio
                      :src="currentWordAudioUrl"
                      controls
                      class="word-audio-player"
                      preload="none"
                    ></audio>
                    <button
                      type="button"
                      class="btn btn-warning btn-small audio-reset-btn"
                      @click="resetWordAudio"
                      :disabled="resettingAudio"
                      title="重置音频"
                    >
                      {{ resettingAudio ? "重置中..." : "🔄 重置" }}
                    </button>
                  </div>
                </div>
              </div>

              <div class="detail-content">
                <div
                  v-for="(item, index) in parsedChatContent"
                  :key="index"
                  class="chat-item"
                  :class="item.type"
                >
                  <div class="chat-type-label">
                    <span :class="['tag', 'tag-' + getTagType(item.type)]">
                      {{ getTypeLabel(item.type) }}
                    </span>
                  </div>

                  <div class="chat-content">
                    <div class="english-text">{{ item.english_text }}</div>
                    <div class="chinese-text">{{ item.chinese_text }}</div>

                    <div
                      v-if="item.voice_url || item.type === 'example'"
                      class="audio-section"
                    >
                      <audio
                        v-if="item.voice_url"
                        :src="item.voice_url"
                        controls
                        class="audio-player"
                      ></audio>

                      <button
                        v-if="item.type === 'example'"
                        type="button"
                        class="btn btn-warning btn-small audio-reset-btn"
                        @click="resetSentenceAudio(item, index)"
                        :disabled="resettingSentenceAudio"
                        title="重置例句音频"
                      >
                        {{
                          resettingSentenceAudio ? "重置中..." : "🔄 重置音频"
                        }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-else class="empty-detail">
              <div class="empty-content">
                <div class="empty-icon">📋</div>
                <div class="empty-text">请选择一个单词查看详情</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 编辑单词详情弹窗 -->
    <div
      v-if="editDialogVisible"
      class="modal-overlay"
      @click="handleEditDialogClose"
    >
      <div class="modal-dialog" @click.stop>
        <div class="modal-header">
          <h3>编辑单词详情</h3>
          <button
            type="button"
            class="modal-close"
            @click="handleEditDialogClose"
          >
            ×
          </button>
        </div>
        <div class="modal-body">
          <div class="edit-dialog-content">
            <div class="word-info-header">
              <h3>{{ selectedWord?.content }}</h3>
              <span class="word-id">ID: {{ selectedWord?.id }}</span>
            </div>

            <div class="edit-items-container">
              <div
                v-for="(item, index) in editingChatContent"
                :key="index"
                class="edit-item"
              >
                <div class="edit-item-header">
                  <select v-model="item.type" class="form-select">
                    <option value="explain">解释</option>
                    <option value="example">例句</option>
                    <option value="other">其他</option>
                  </select>
                  <button
                    type="button"
                    class="btn btn-danger btn-small"
                    @click="removeEditItem(index)"
                    :disabled="editingChatContent.length <= 1"
                  >
                    删除
                  </button>
                </div>

                <div class="edit-item-content">
                  <div class="edit-field">
                    <label>英文内容:</label>
                    <textarea
                      v-model="item.english_text"
                      class="form-textarea"
                      rows="2"
                      placeholder="请输入英文内容"
                    ></textarea>
                  </div>

                  <div class="edit-field">
                    <label>中文翻译:</label>
                    <textarea
                      v-model="item.chinese_text"
                      class="form-textarea"
                      rows="2"
                      placeholder="请输入中文翻译"
                    ></textarea>
                  </div>

                  <div class="edit-field" v-if="item.type === 'example'">
                    <label>音频链接:</label>
                    <input
                      type="text"
                      v-model="item.voice_url"
                      class="form-input"
                      placeholder="请输入音频URL (可选)"
                    />
                  </div>
                </div>
              </div>

              <div class="add-item-section">
                <button
                  type="button"
                  class="btn btn-success"
                  @click="addEditItem"
                >
                  ➕ 添加新项目
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-default"
            @click="handleEditDialogClose"
          >
            取消
          </button>
          <button
            type="button"
            class="btn btn-primary"
            @click="saveWordDetail"
            :disabled="savingDetail"
          >
            {{ savingDetail ? "保存中..." : "保存" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { word, idol } from "../../../api/index";

export default {
  name: "WordTable",
  data() {
    return {
      loading: false,
      bookLoading: false,
      searchForm: {
        bookId: null,
        idolId: 1,
        pageSize: 10,
        pageNum: 1,
        content: "", // 单词搜索内容
      },
      wordList: [],
      selectedWord: null,
      total: 0,
      voiceSource: "", // 音频源URL
      currentPage: 1,
      pageSize: 10,
      bookList: [], // 存储单词书列表
      selectedCategory: null, // 当前选中的分类
      dropdownVisible: false, // 下拉框是否可见
      idolList: [], // 存储爱豆列表
      selectedGroup: null, // 当前选中的团体
      idolDropdownVisible: false, // 爱豆下拉框是否可见
      editDialogVisible: false, // 编辑弹窗是否可见
      editingChatContent: [], // 编辑中的聊天内容
      savingDetail: false, // 保存详情loading状态
      resettingAudio: false, // 重置音频loading状态
      resettingSentenceAudio: false, // 重置例句音频loading状态
    };
  },

  computed: {
    totalPages() {
      return Math.ceil(this.total / this.pageSize);
    },

    parsedChatContent() {
      if (!this.selectedWord?.chatContent) return [];

      try {
        return JSON.parse(this.selectedWord.chatContent);
      } catch (error) {
        console.error("解析chatContent失败:", error);
        return [];
      }
    },

    currentBooks() {
      if (
        this.selectedCategory === null ||
        this.selectedCategory === undefined
      ) {
        return [];
      }

      // 确保数据类型匹配，将 selectedCategory 转换为数字进行比较
      const selectedCategoryNum = Number(this.selectedCategory);

      const category = this.bookList.find(
        (item) => Number(item.bookType) === selectedCategoryNum
      );

      return category ? category.books : [];
    },

    selectedBookName() {
      if (!this.searchForm.bookId) return "";

      for (const group of this.bookList) {
        const book = group.books.find(
          (book) => book.bookId === this.searchForm.bookId
        );
        if (book) {
          return book.bookName;
        }
      }
      return "";
    },

    currentIdols() {
      if (!this.selectedGroup) return [];
      const group = this.idolList.find(
        (item) => Number(item.groupId) === Number(this.selectedGroup)
      );
      return group ? group.idols : [];
    },

    selectedIdolName() {
      if (!this.searchForm.idolId) return "";

      for (const group of this.idolList) {
        const idol = group.idols.find(
          (idol) => idol.idolId === this.searchForm.idolId
        );
        if (idol) {
          return idol.idolName;
        }
      }
      return "";
    },

    // 当前选中单词的音频URL
    currentWordAudioUrl() {
      if (
        !this.selectedWord ||
        !this.voiceSource ||
        !this.selectedWord.audioFile
      )
        return "";
      return `${this.voiceSource}${this.selectedWord.audioFile}`;
    },
  },

  created() {
    this.loadBookList();
    this.loadIdolList();
  },

  mounted() {
    // 点击外部关闭下拉框
    document.addEventListener("click", this.handleClickOutside);
  },

  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
  },

  methods: {
    async loadBookList() {
      this.bookLoading = true;
      try {
        const response = await word.showBookList({});

        if (response.code === 200) {
          this.bookList = response.data || [];

          // 默认选择第一个分类和第一个单词书
          if (this.bookList.length > 0 && this.bookList[0].books.length > 0) {
            this.selectedCategory = this.bookList[0].bookType;
            this.searchForm.bookId = this.bookList[0].books[0].bookId;

            // 加载单词书选择后再加载单词
            this.loadWords();
          }
        } else {
          this.$message.error("加载单词书失败");
        }
      } catch (error) {
        console.error("加载单词书失败:", error);
        this.$message.error("加载单词书失败");
      } finally {
        this.bookLoading = false;
      }
    },

    async loadWords() {
      // 如果有单词搜索内容，使用搜索API
      if (this.searchForm.content && this.searchForm.content.trim()) {
        await this.searchWordsByContent();
        return;
      }

      // 否则使用原来的逻辑
      if (!this.searchForm.bookId) {
        this.$message.warning("请先选择单词书");
        return;
      }

      this.loading = true;
      try {
        const requestData = {
          ...this.searchForm,
          pageNum: this.currentPage,
          pageSize: this.pageSize,
        };

        const response = await word.showWordChat(requestData);

        console.log(response);
        if (response.code === 200) {
          this.wordList = response.data.data || [];
          this.total = response.data.total || 0;
          // 获取音频源URL
          this.voiceSource = response.data.voiceSource || "";

          // 自动选择第一个单词
          if (this.wordList.length > 0 && !this.selectedWord) {
            this.selectedWord = this.wordList[0];
            console.log("选中的单词数据:", this.selectedWord);
            console.log("单词ID:", this.selectedWord.wordId);
          }
        }
      } catch (error) {
        console.error("加载单词失败:", error);
        this.$message.error("加载单词失败");
      } finally {
        this.loading = false;
      }
    },

    async searchWordsByContent() {
      this.loading = true;
      try {
        const requestData = {
          content: this.searchForm.content.trim(),
          idolId: this.searchForm.idolId,
          pageNum: this.currentPage,
          pageSize: this.pageSize,
        };

        const response = await word.getWordChatDTO(requestData);

        console.log("单词搜索结果:", response);
        if (response.code === 200) {
          this.wordList = response.data.data || [];
          this.total = response.data.total || 0;
          // 获取音频源URL
          this.voiceSource = response.data.voiceSource || "";

          // 自动选择第一个单词
          if (this.wordList.length > 0) {
            this.selectedWord = this.wordList[0];
            console.log("搜索选中的单词数据:", this.selectedWord);
            console.log("搜索单词ID:", this.selectedWord.wordId);
          } else {
            this.selectedWord = null;
          }

          if (this.wordList.length === 0) {
            this.$message.info(`未找到包含"${this.searchForm.content}"的单词`);
          }
        } else {
          this.$message.error(response.msg || "搜索失败");
        }
      } catch (error) {
        console.error("搜索单词失败:", error);
        this.$message.error("搜索单词失败");
      } finally {
        this.loading = false;
      }
    },

    async loadIdolList() {
      try {
        const response = await idol.getIdolList({});

        if (response.code === 200) {
          this.idolList = response.data || [];

          // 默认选择第一个团体和第一个爱豆
          if (this.idolList.length > 0 && this.idolList[0].idols.length > 0) {
            this.selectedGroup = this.idolList[0].groupId;
            this.searchForm.idolId = this.idolList[0].idols[0].idolId;
          }
        } else {
          this.$message.error("加载爱豆列表失败");
        }
      } catch (error) {
        console.error("加载爱豆列表失败:", error);
        this.$message.error("加载爱豆列表失败");
      }
    },

    toggleDropdown() {
      this.dropdownVisible = !this.dropdownVisible;
    },

    selectCategory(categoryType) {
      this.selectedCategory = categoryType;
    },

    selectBook(book) {
      this.searchForm.bookId = book.bookId;
      this.dropdownVisible = false;
      this.onBookChange();
    },

    toggleIdolDropdown() {
      this.idolDropdownVisible = !this.idolDropdownVisible;
    },

    selectGroup(groupId) {
      this.selectedGroup = groupId;
    },

    selectIdol(idol) {
      this.searchForm.idolId = idol.idolId;
      this.idolDropdownVisible = false;
      this.onIdolChange();
    },

    onIdolChange() {
      // 爱豆改变时重置页码并重新加载
      this.currentPage = 1;
      this.searchForm.pageNum = 1;
      this.selectedWord = null;
      // 如果有单词搜索内容，则使用搜索，否则使用单词书加载
      this.loadWords();
    },

    onBookChange() {
      // 单词书改变时重置页码、清空搜索内容并重新加载
      this.currentPage = 1;
      this.searchForm.pageNum = 1;
      this.searchForm.content = ""; // 清空搜索内容
      this.selectedWord = null;
      this.loadWords();
    },

    selectWord(word) {
      this.selectedWord = word;
      console.log("手动选中的单词数据:", this.selectedWord);
      console.log("手动选中单词ID:", this.selectedWord.wordId);
    },

    clearSearch() {
      this.searchForm.content = "";
      this.currentPage = 1;
      this.searchForm.pageNum = 1;
      this.selectedWord = null;
      this.loadWords();
    },

    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.searchForm.pageNum = this.currentPage;
        this.selectedWord = null;
        this.loadWords();
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.searchForm.pageNum = this.currentPage;
        this.selectedWord = null;
        this.loadWords();
      }
    },

    getTagType(type) {
      const typeMap = {
        explain: "primary",
        example: "success",
        other: "warning",
      };
      return typeMap[type] || "info";
    },

    getTypeLabel(type) {
      const labelMap = {
        explain: "解释",
        example: "例句",
        other: "其他",
      };
      return labelMap[type] || type;
    },

    handleClickOutside(event) {
      const bookSelectElement = this.$el.querySelector(".custom-book-select");
      const idolSelectElement = this.$el.querySelector(".custom-idol-select");

      if (bookSelectElement && !bookSelectElement.contains(event.target)) {
        this.dropdownVisible = false;
      }

      if (idolSelectElement && !idolSelectElement.contains(event.target)) {
        this.idolDropdownVisible = false;
      }
    },

    // 编辑单词详情相关方法
    openEditDialog() {
      if (!this.selectedWord || !this.selectedWord.chatContent) {
        this.$message.warning("没有可编辑的内容");
        return;
      }

      try {
        // 深拷贝当前的chatContent用于编辑
        this.editingChatContent = JSON.parse(
          JSON.stringify(this.parsedChatContent)
        );
        this.editDialogVisible = true;
      } catch (error) {
        console.error("解析聊天内容失败:", error);
        this.$message.error("内容解析失败");
      }
    },

    handleEditDialogClose() {
      this.editDialogVisible = false;
      this.editingChatContent = [];
    },

    addEditItem() {
      this.editingChatContent.push({
        type: "explain",
        english_text: "",
        chinese_text: "",
        voice_url: "",
      });
    },

    removeEditItem(index) {
      if (this.editingChatContent.length > 1) {
        this.editingChatContent.splice(index, 1);
      }
    },

    async saveWordDetail() {
      // 验证编辑内容
      const hasEmptyContent = this.editingChatContent.some(
        (item) => !item.english_text.trim() || !item.chinese_text.trim()
      );

      if (hasEmptyContent) {
        this.$message.warning("请填写完整的英文和中文内容");
        return;
      }

      this.savingDetail = true;

      try {
        // 准备保存的数据格式
        const saveData = {
          id: this.selectedWord.id,
          jsonString: JSON.stringify(this.editingChatContent),
        };

        console.log("保存的数据:", saveData);

        // 调用保存API
        const response = await word.editWordChat(saveData);

        if (response.code === 200) {
          // 更新本地数据
          this.selectedWord.chatContent = JSON.stringify(
            this.editingChatContent
          );

          this.$message.success("保存成功");
          this.handleEditDialogClose();
        } else {
          this.$message.error(response.msg || "保存失败，请重试");
        }
      } catch (error) {
        console.error("保存失败:", error);
        this.$message.error("保存失败，请重试");
      } finally {
        this.savingDetail = false;
      }
    },

    // 重置单词音频
    async resetWordAudio() {
      if (!this.selectedWord) {
        this.$message.warning("请先选择一个单词");
        return;
      }

      this.resettingAudio = true;

      try {
        const resetData = {
          id: this.selectedWord.id,
          wordId: this.selectedWord.wordId,
          url: this.currentWordAudioUrl,
          content: this.selectedWord.content,
          idolId: this.searchForm.idolId,
        };

        console.log("重置音频数据:", resetData);

        // 调用重置音频API
        const response = await word.updateWordAudio(resetData);

        if (response.code === 200) {
          this.$message.success("音频重置成功");
        } else {
          this.$message.error(response.msg || "音频重置失败，请重试");
        }
      } catch (error) {
        console.error("重置音频失败:", error);
        this.$message.error("音频重置失败，请重试");
      } finally {
        this.resettingAudio = false;
      }
    },

    // 重置例句音频
    async resetSentenceAudio(item, index) {
      if (!item.english_text || !this.selectedWord) {
        this.$message.warning("例句内容不能为空");
        return;
      }

      if (!this.selectedWord.id) {
        this.$message.warning("单词ID缺失，无法重置音频");
        console.error("selectedWord.id is missing:", this.selectedWord);
        return;
      }

      this.resettingSentenceAudio = true;

      try {
        const resetData = {
          id: this.selectedWord.id,
          idolId: this.searchForm.idolId, // 使用当前选中的idolId作为voice_id
          content: this.selectedWord.content, // 当前例句对应的单词
          sentence: item.english_text, // 例句内容
          index: index, // 例句在json列表的序号
          wordId: this.selectedWord.wordId, // 单词ID
        };

        // 调用重置例句音频API
        const response = await word.updateSentenceAudio(resetData);

        if (response.code === 200) {
          // 更新例句的音频URL
          if (response.data && response.data.voice_url) {
            // 更新parsedChatContent中的数据
            item.voice_url = response.data.voice_url;

            // 同时更新selectedWord的chatContent
            try {
              const chatContent = JSON.parse(this.selectedWord.chatContent);
              if (chatContent[index]) {
                chatContent[index].voice_url = response.data.voice_url;
                this.selectedWord.chatContent = JSON.stringify(chatContent);
              }
            } catch (error) {
              console.error("更新chatContent失败:", error);
            }
          }

          this.$message.success("例句音频重置成功");
        } else {
          this.$message.error(response.msg || "例句音频重置失败，请重试");
        }
      } catch (error) {
        console.error("重置例句音频失败:", error);
        this.$message.error("例句音频重置失败，请重试");
      } finally {
        this.resettingSentenceAudio = false;
      }
    },
  },
};
</script>

<style scoped>
.word-table-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: #faf9de;
  overflow: hidden;
}

.search-bar {
  background: #fff;
  padding: 16px 20px;
  border-bottom: 1px solid #e4e7ed;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  margin: 10px;
  flex-shrink: 0;
}

.search-form {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}

.form-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.form-item label {
  font-weight: 600;
  color: #2c3e50;
  font-size: 14px;
  white-space: nowrap;
}

/* 按钮样式 */
.btn {
  padding: 8px 16px;
  border: 1px solid;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none !important;
}

.btn-primary {
  background-color: #ffd700;
  border-color: #ffd700;
  color: #000;
}

.btn-primary:hover:not(:disabled) {
  background-color: #e6c200;
  border-color: #e6c200;
  transform: translateY(-1px);
}

.btn-pagination {
  background-color: #ffd700;
  border-color: #ffd700;
  color: #000;
}

.btn-pagination:hover:not(:disabled) {
  background-color: #e6c200;
  border-color: #e6c200;
  transform: translateY(-1px);
}

.btn-pagination:disabled {
  background-color: #ccc;
  border-color: #ccc;
  color: #888;
}

.btn-small {
  padding: 4px 8px;
  font-size: 12px;
}

.btn-danger {
  background-color: #ff6b6b;
  border-color: #ff6b6b;
  color: white;
}

.btn-danger:hover:not(:disabled) {
  background-color: #ff5252;
  border-color: #ff5252;
}

.btn-success {
  background-color: #67c23a;
  border-color: #67c23a;
  color: white;
}

.btn-success:hover:not(:disabled) {
  background-color: #5daf34;
  border-color: #5daf34;
}

.btn-default {
  background-color: #f5f5f5;
  border-color: #ddd;
  color: #333;
}

.btn-default:hover:not(:disabled) {
  background-color: #e8e8e8;
  border-color: #ccc;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 12px;
}

.page-info {
  font-size: 14px;
  color: #2c3e50;
  font-weight: 600;
  min-width: 60px;
  text-align: center;
}

.content-area {
  flex: 1;
  display: flex;
  gap: 16px;
  padding: 10px;
  overflow: hidden;
  min-height: 0;
}

.word-list-container {
  width: 400px;
  flex-shrink: 0;
}

.word-detail-container {
  flex: 1;
  min-width: 0;
}

.word-list-card,
.word-detail-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: none;
  background: white;
}

.card-header {
  background: linear-gradient(135deg, #ffd700 0%, #ffed4a 100%);
  border-radius: 12px 12px 0 0;
  padding: 16px 20px;
  border-bottom: 1px solid #e6e6e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header span {
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
}

.card-body {
  flex: 1;
  overflow: hidden;
  padding: 0;
}

.word-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.word-count {
  font-size: 12px;
  color: #666;
  background: #ff6b35;
  padding: 4px 8px;
  border-radius: 12px;
  color: white;
  font-weight: 500;
}

.word-list {
  height: 100%;
  overflow-y: auto;
  padding: 12px;
}

.word-item {
  padding: 12px 16px;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: white;
}

.word-item:hover {
  border-color: #ffd700;
  box-shadow: 0 2px 8px rgba(255, 215, 0, 0.2);
  transform: translateY(-1px);
}

.word-item.active {
  border-color: #ffd700;
  background-color: #fff9c4;
  box-shadow: 0 2px 8px rgba(255, 215, 0, 0.3);
}

.word-content {
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 4px;
}

.word-id {
  font-size: 12px;
  color: #666;
}

.word-detail {
  height: 100%;
  overflow-y: auto;
  padding: 16px;
}

.word-pronunciation-section {
  margin-bottom: 20px;
  padding: 16px 20px;
  background: linear-gradient(135deg, #fff9c4 0%, #ffeaa7 100%);
  border-radius: 12px;
  border-left: 4px solid #ffd700;
  box-shadow: 0 2px 8px rgba(255, 215, 0, 0.2);
}

.word-main {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.word-content {
  font-size: 24px;
  font-weight: bold;
  color: #2c3e50;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.1);
}

.word-pronunciation {
  font-size: 18px;
  color: #e17055;
  font-family: "Courier New", monospace;
  font-weight: 600;
}

.word-pronunciation.placeholder {
  color: #999;
  font-style: italic;
  font-size: 16px;
}

/* 表单元素样式 */
.form-input,
.form-textarea,
.form-select {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.3s ease;
  font-family: inherit;
}

.search-input-group {
  display: flex;
  align-items: center;
  gap: 4px;
  position: relative;
}

.audio-input-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.audio-input-group .form-input {
  flex: 1;
}

.search-input {
  width: 200px;
  min-width: 150px;
}

.btn-clear {
  padding: 4px 8px;
  background: #f5f5f5;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  color: #666;
  font-size: 12px;
  line-height: 1;
  min-width: auto;
}

.btn-clear:hover {
  background: #e8e8e8;
  color: #333;
}

.form-input:focus,
.form-textarea:focus,
.form-select:focus {
  outline: none;
  border-color: #ffd700;
  box-shadow: 0 0 0 2px rgba(255, 215, 0, 0.2);
}

.form-textarea {
  resize: vertical;
  min-height: 60px;
}

.form-select {
  cursor: pointer;
  background-color: white;
  color: #2c3e50;
}

.form-select option {
  color: #2c3e50;
  background-color: white;
  padding: 8px 12px;
}

.form-select option:hover,
.form-select option:focus {
  background-color: #fff9c4;
  color: #2c3e50;
}

.form-select option:checked,
.form-select option:selected {
  background-color: #ffd700;
  color: #000;
  font-weight: 600;
}

/* 标签样式 */
.tag {
  display: inline-block;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid;
}

.tag-primary {
  background-color: #fff9c4;
  color: #b8860b;
  border-color: #ffd700;
}

.tag-success {
  background-color: #f0f9ff;
  color: #67c23a;
  border-color: #e1f3d8;
}

.tag-warning {
  background-color: #fdf6ec;
  color: #e6a23c;
  border-color: #faecd8;
}

.tag-info {
  background-color: #f4f4f5;
  color: #909399;
  border-color: #e9e9eb;
}

/* 加载和空状态样式 */
.loading-state,
.empty-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #666;
}

.loading-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #ffd700;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 10px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.loading-text,
.empty-text {
  font-size: 14px;
  margin-top: 8px;
}

.empty-icon {
  font-size: 32px;
  margin-bottom: 8px;
}

/* 模态框样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.modal-dialog {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.modal-header {
  padding: 20px;
  border-bottom: 1px solid #e4e7ed;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, #ffd700 0%, #ffed4a 100%);
  border-radius: 12px 12px 0 0;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
}

.modal-close {
  background: none;
  border: none;
  font-size: 24px;
  color: #666;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.modal-close:hover {
  background-color: rgba(0, 0, 0, 0.1);
  color: #333;
}

.modal-body {
  padding: 20px;
  flex: 1;
  overflow-y: auto;
}

.modal-footer {
  padding: 20px;
  border-top: 1px solid #e4e7ed;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.word-audio-section {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
}

.word-audio-player {
  height: 32px;
  border-radius: 6px;
  outline: none;
}

.audio-reset-btn {
  font-size: 11px;
  padding: 4px 6px;
  min-width: auto;
  white-space: nowrap;
}

.detail-content {
  max-width: 100%;
}

.chat-item {
  margin-bottom: 20px;
  padding: 16px;
  border-radius: 12px;
  border-left: 4px solid #e4e7ed;
  background: #f8f9fa;
  transition: all 0.3s ease;
}

.chat-item:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.chat-item.explain {
  border-left-color: #ffd700;
  background-color: #fff9c4;
}

.chat-item.example {
  border-left-color: #67c23a;
  background-color: #e8f5e8;
}

.chat-item.other {
  border-left-color: #ff6b35;
  background-color: #fdf6ec;
}

.chat-type-label {
  margin-bottom: 8px;
}

.chat-content {
  line-height: 1.6;
}

.english-text {
  font-size: 14px;
  color: #2c3e50;
  margin-bottom: 6px;
  font-weight: 500;
}

.chinese-text {
  font-size: 13px;
  color: #666;
  margin-bottom: 8px;
}

.audio-section {
  margin-top: 8px;
}

.audio-player {
  width: 100%;
  max-width: 300px;
  height: 32px;
  border-radius: 6px;
}

.empty-state,
.empty-detail {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: #666;
  font-size: 16px;
}

/* 滚动条样式 */
.word-list::-webkit-scrollbar,
.word-detail::-webkit-scrollbar {
  width: 6px;
}

.word-list::-webkit-scrollbar-track,
.word-detail::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
}

.word-list::-webkit-scrollbar-thumb,
.word-detail::-webkit-scrollbar-thumb {
  background: rgba(255, 215, 0, 0.3);
  border-radius: 10px;
}

.word-list::-webkit-scrollbar-thumb:hover,
.word-detail::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 215, 0, 0.5);
}

/* 自定义单词书选择器 */
.custom-book-select {
  position: relative;
  width: 300px;
}

.select-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
  min-height: 32px;
}

.select-trigger:hover {
  border-color: #c0c4cc;
}

.select-trigger.active {
  border-color: #ffd700;
  box-shadow: 0 0 0 2px rgba(255, 215, 0, 0.2);
}

.select-value {
  flex: 1;
  font-size: 14px;
  color: #2c3e50;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.select-arrow {
  font-size: 12px;
  color: #c0c4cc;
  transition: transform 0.3s ease;
  margin-left: 8px;
}

.select-arrow.arrow-up {
  transform: rotate(180deg);
}

.select-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  background: white;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  margin-top: 4px;
}

.dropdown-container {
  display: flex;
  width: 100%;
  height: 300px;
  border-radius: 6px;
  overflow: hidden;
}

/* 左侧分类栏 */
.book-categories {
  width: 140px;
  background: #f8f9fa;
  border-right: 1px solid #e9ecef;
  overflow-y: auto;
}

.category-item {
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  border-bottom: 1px solid #e9ecef;
  font-size: 14px;
  font-weight: 500;
  color: #666;
}

.category-item:hover {
  background-color: #fff9c4;
  color: #2c3e50;
}

.category-item.active {
  background: linear-gradient(135deg, #ffd700 0%, #ffed4a 100%);
  color: #2c3e50;
  font-weight: 600;
}

/* 右侧选项栏 */
.book-options {
  flex: 1;
  overflow-y: auto;
  background: white;
}

.book-option {
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  border-bottom: 1px solid #f0f0f0;
  font-size: 14px;
  font-weight: 500;
  color: #2c3e50;
}

.book-option:hover {
  background-color: #fff9c4;
  color: #2c3e50;
}

.book-option.selected {
  background-color: #ffd700;
  color: #000;
  font-weight: 600;
}

.no-books {
  padding: 20px;
  text-align: center;
  color: #999;
  font-size: 14px;
}

/* 滚动条样式 */
.book-categories::-webkit-scrollbar,
.book-options::-webkit-scrollbar {
  width: 6px;
}

.book-categories::-webkit-scrollbar-track,
.book-options::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.book-categories::-webkit-scrollbar-thumb,
.book-options::-webkit-scrollbar-thumb {
  background: rgba(255, 215, 0, 0.3);
  border-radius: 3px;
}

.book-categories::-webkit-scrollbar-thumb:hover,
.book-options::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 215, 0, 0.5);
}

/* 自定义爱豆选择器样式 */
.custom-idol-select {
  position: relative;
  width: 280px;
}

.custom-idol-select .select-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
  min-height: 32px;
}

.custom-idol-select .select-trigger:hover {
  border-color: #c0c4cc;
}

.custom-idol-select .select-trigger.active {
  border-color: #ffd700;
  box-shadow: 0 0 0 2px rgba(255, 215, 0, 0.2);
}

.custom-idol-select .select-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  background: white;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  margin-top: 4px;
}

/* 左侧团体栏 */
.idol-groups {
  width: 110px;
  background: #f8f9fa;
  border-right: 1px solid #e9ecef;
  overflow-y: auto;
}

.group-item {
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  border-bottom: 1px solid #e9ecef;
  font-size: 14px;
  font-weight: 500;
  color: #666;
}

.group-item:hover {
  background-color: #fff9c4;
  color: #2c3e50;
}

.group-item.active {
  background: linear-gradient(135deg, #ffd700 0%, #ffed4a 100%);
  color: #2c3e50;
  font-weight: 600;
}

/* 右侧爱豆选项栏 */
.idol-options {
  flex: 1;
  overflow-y: auto;
  background: white;
}

.idol-option {
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  border-bottom: 1px solid #f0f0f0;
  font-size: 14px;
  font-weight: 500;
  color: #2c3e50;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.idol-option:hover {
  background-color: #fff9c4;
  color: #2c3e50;
}

.idol-option.selected {
  background-color: #ffd700;
  color: #000;
  font-weight: 600;
}

.no-idols {
  padding: 20px;
  text-align: center;
  color: #999;
  font-size: 14px;
}

/* 爱豆选择器滚动条样式 */
.idol-groups::-webkit-scrollbar,
.idol-options::-webkit-scrollbar {
  width: 6px;
}

.idol-groups::-webkit-scrollbar-track,
.idol-options::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.idol-groups::-webkit-scrollbar-thumb,
.idol-options::-webkit-scrollbar-thumb {
  background: rgba(255, 215, 0, 0.3);
  border-radius: 3px;
}

.idol-groups::-webkit-scrollbar-thumb:hover,
.idol-options::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 215, 0, 0.5);
}

/* 编辑单词详情弹窗样式 */
.edit-dialog-content {
  max-height: 600px;
  overflow-y: auto;
}

.word-info-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  border-bottom: 2px solid #ffd700;
  margin-bottom: 20px;
}

.word-info-header h3 {
  margin: 0;
  color: #2c3e50;
  font-size: 24px;
  font-weight: 600;
}

.word-id {
  font-size: 14px;
  color: #666;
  background: #f8f9fa;
  padding: 4px 8px;
  border-radius: 4px;
}

.edit-items-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.edit-item {
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  padding: 16px;
  background: #f8f9fa;
  transition: all 0.3s ease;
}

.edit-item:hover {
  border-color: #ffd700;
  box-shadow: 0 2px 8px rgba(255, 215, 0, 0.2);
}

.edit-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.edit-item-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.edit-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.edit-field label {
  font-weight: 600;
  color: #2c3e50;
  font-size: 14px;
}

.add-item-section {
  text-align: center;
  padding: 20px 0;
  border-top: 1px solid #e4e7ed;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .content-area {
    flex-direction: column;
  }

  .word-list-container {
    width: 100%;
    height: 300px;
  }

  .word-detail-container {
    height: 400px;
  }

  .search-bar {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }

  .pagination-controls {
    justify-content: center;
  }
}

@media (max-width: 768px) {
  .search-bar {
    margin: 5px;
    padding: 12px;
  }

  .content-area {
    padding: 5px;
    gap: 10px;
  }

  .word-list-container {
    height: 250px;
  }

  .word-detail-container {
    height: 350px;
  }

  .word-item {
    padding: 10px 12px;
  }

  .word-content {
    font-size: 14px;
  }

  .chat-item {
    padding: 12px;
  }

  .english-text {
    font-size: 13px;
  }

  .chinese-text {
    font-size: 12px;
  }
}
</style>
